// Alternative approach with scroll-based active states
'use client'

import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Globe, DollarSign, ChevronDown } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { useCurrency } from '../../contexts/CurrencyContext'
import LanguageSwitcher from '../../LanguageSwitcher'

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('')
  
  const { selectedCurrency, currencies, changeCurrency } = useCurrency()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  // Track active section on home page
  useEffect(() => {
    if (location.pathname === '/') {
      const handleScroll = () => {
        const sections = ['about-us-home', 'services', 'contact']
        const currentSection = sections.find(section => {
          const element = document.getElementById(section)
          if (element) {
            const rect = element.getBoundingClientRect()
            return rect.top <= 100 && rect.bottom >= 100
          }
          return false
        })
        setActiveSection(currentSection || '')
      }

      window.addEventListener('scroll', handleScroll)
      handleScroll() // Check initial position
      
      return () => window.removeEventListener('scroll', handleScroll)
    }
  }, [location.pathname])

  // Helper function to determine if a navigation item is active
  const isActive = (type, path = null, section = null) => {
    if (type === 'route') {
      return location.pathname === path
    }
    if (type === 'section' && location.pathname === '/') {
      return activeSection === section
    }
    return false
  }

  // Function to handle navigation to home sections
  const handleSectionNavigation = (sectionId) => {
    setIsMenuOpen(false)
    
    if (location.pathname !== '/') {
      navigate('/')
      setTimeout(() => {
        const element = document.getElementById(sectionId)
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          })
        }
      }, 100)
    } else {
      const element = document.getElementById(sectionId)
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        })
      }
    }
  }

  const handleCurrencyChange = (currency) => {
    changeCurrency(currency)
    setIsCurrencyOpen(false)
  }

  return (
    <header className="bg-transparent text-white absolute top-0 left-0 w-full z-50 font-poppins">
      <div className="pl-4 sm:px-6 md:px-8 lg:pl-12 xl:px-[120px] py-3 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
          <img src="/images/logo.png" alt="Blue Road Lux Logo" className="h-12 sm:h-14 rounded-lg" />
        </Link>

        {/* Desktop Navigation - Centered */}
        <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8 absolute left-1/2 transform -translate-x-1/2">
          <Link 
            to="/" 
            className={`font-medium transition-colors ${
              isActive('route', '/') 
                ? 'text-white' 
                : 'text-gray-300 hover:text-blue-400'
            }`}
          >
            {t('home')}
          </Link>
          <Link 
            to="/cars" 
            className={`font-medium transition-colors ${
              isActive('route', '/cars') 
                ? 'text-white' 
                : 'text-gray-300 hover:text-blue-400'
            }`}
          >
            {t('cars')}
          </Link>
          <button 
            onClick={() => handleSectionNavigation('about-us-home')}
            className={`font-medium transition-colors cursor-pointer ${
              isActive('section', null, 'about-us-home') 
                ? 'text-white' 
                : 'text-gray-300 hover:text-blue-400'
            }`}
          >
            {t('aboutus')}
          </button>
          <button 
            onClick={() => handleSectionNavigation('services')}
            className={`font-medium transition-colors cursor-pointer ${
              isActive('section', null, 'services') 
                ? 'text-white' 
                : 'text-gray-300 hover:text-blue-400'
            }`}
          >
            {t('services')}
          </button>
          <button 
            onClick={() => handleSectionNavigation('contact')}
            className={`font-medium transition-colors cursor-pointer ${
              isActive('section', null, 'contact') 
                ? 'text-white' 
                : 'text-gray-300 hover:text-blue-400'
            }`}
          >
            {t('contact')}
          </button>
        </nav>

        {/* Language and Currency selectors */}
        <div className="hidden lg:flex items-center space-x-4 flex-shrink-0">
          {/* Language Switcher with Globe Icon */}
          <div className="flex items-center space-x-2">
            <img src="/images/icons/language.png" alt="Language" className="w-4 h-4" />
            <LanguageSwitcher />
          </div>

          {/* Currency Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
              className="flex items-center space-x-2 py-2 transition-colors text-sm font-medium"
            >
              <span className="text-white">{selectedCurrency.symbol}</span>
            </button>

            {/* Currency Dropdown Menu */}
            {isCurrencyOpen && (
              <div className="absolute right-0 mt-2 w-24 bg-gray-800 border border-gray-700 rounded-md shadow-lg z-50">
                {currencies.map((currency) => (
                  <button
                    key={currency.code}
                    onClick={() => handleCurrencyChange(currency)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-700 transition-colors ${
                      selectedCurrency.code === currency.code 
                        ? 'text-blue-400 bg-gray-700' 
                        : 'text-gray-300'
                    }`}
                  >
                    <span className="flex items-center space-x-2">
                      <span>{currency.symbol}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile menu button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="lg:hidden p-2 rounded-md hover:bg-gray-800 transition-colors flex-shrink-0"
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Navigation with same active logic */}
      {isMenuOpen && (
        <div className="lg:hidden mt-4 pb-4 border-t border-gray-800 bg-black/90 backdrop-blur-sm">
          <nav className="flex flex-col space-y-4 pt-4 px-4 sm:px-6">
            <Link 
              to="/" 
              className={`font-medium transition-colors ${
                isActive('route', '/') 
                  ? 'text-white' 
                  : 'text-gray-300 hover:text-blue-400'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t('home')}
            </Link>
            <Link 
              to="/cars" 
              className={`font-medium transition-colors ${
                isActive('route', '/cars') 
                  ? 'text-white' 
                  : 'text-gray-300 hover:text-blue-400'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t('cars')}
            </Link>
            <button 
              onClick={() => handleSectionNavigation('about')}
              className={`font-medium transition-colors text-left ${
                isActive('section', null, 'about-us-home') 
                  ? 'text-white' 
                  : 'text-gray-300 hover:text-blue-400'
              }`}
            >
              {t('aboutus')}
            </button>
            <button 
              onClick={() => handleSectionNavigation('services')}
              className={`font-medium transition-colors text-left ${
                isActive('section', null, 'services') 
                  ? 'text-white' 
                  : 'text-gray-300 hover:text-blue-400'
              }`}
            >
              {t('services')}
            </button>
            <button 
              onClick={() => handleSectionNavigation('contact')}
              className={`font-medium transition-colors text-left ${
                isActive('section', null, 'contact') 
                  ? 'text-white' 
                  : 'text-gray-300 hover:text-blue-400'
              }`}
            >
              {t('contact')}
            </button>
            
            {/* Mobile Language and Currency Selectors */}
            <div className="flex flex-col space-y-3 pt-2 border-t border-gray-700">
              <div className="flex items-center space-x-2">
                <Globe className="w-4 h-4 text-gray-300" />
                <LanguageSwitcher />
              </div>

              <div className="relative">
                <button
                  onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md bg-gray-800/50 hover:bg-gray-700/50 transition-colors text-sm font-medium w-full"
                >
                  <DollarSign className="w-4 h-4 text-gray-300" />
                  <span className="text-white">{selectedCurrency.symbol}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-300 transition-transform ml-auto ${isCurrencyOpen ? 'rotate-180' : ''}`} />
                </button>

                {isCurrencyOpen && (
                  <div className="mt-2 w-full bg-gray-800 border border-gray-700 rounded-md shadow-lg">
                    {currencies.map((currency) => (
                      <button
                        key={currency.code}
                        onClick={() => handleCurrencyChange(currency)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-700 transition-colors ${
                          selectedCurrency.code === currency.code 
                            ? 'text-blue-400 bg-gray-700' 
                            : 'text-gray-300'
                        }`}
                      >
                        <span className="flex items-center space-x-2">
                          <span>{currency.symbol}</span>
                          <span>{currency.code}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}